import CloseIcon from '@src/svgs/CloseIcon';
import { formatDateTime } from '@src/utility/dateFormat';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import type { ClipVideoCreatorAdapter } from './hooks/ClipVideoCreatorAdapter';
import type { ReactNode, SyntheticEvent } from 'react';

/**
 * Clip History preview, seen from outside the History tab. A previewed version
 * is read-only on every tab — it can be watched and its transcript read, but
 * only Restore makes it editable — so the departure from the live clip is
 * labelled wherever the user is, the way Published Version is, and edits are
 * refused with an explanation rather than silently.
 */

/** Above the player: what is showing, plus the two ways out. */
export const ClipVersionPreviewTag = observer(
  ({ adapter }: { adapter: ClipVideoCreatorAdapter }) => {
    const version = adapter.versionPreview.previewingVersion;
    if (!version) return null;
    return (
      <Tag data-testid="clip-version-preview-tag">
        <Label>
          Older Version
          <Detail>{` · ${formatDateTime(version.meta.created_at)}`}</Detail>
        </Label>
        <RestoreLink type="button" onClick={adapter.versionPreview.commit}>
          Restore
        </RestoreLink>
        <ExitButton
          type="button"
          aria-label="Back to Current"
          title="Back to Current"
          onClick={adapter.versionPreview.exit}
        >
          <CloseIcon width={10} height={10} />
        </ExitButton>
      </Tag>
    );
  },
);

/**
 * Wraps a tab whose edits bypass the editor's command guard (templates, reel
 * structure, languages). While a version is previewed the tab still shows and
 * scrolls, but a click or typed input in it is stopped before it reaches the
 * tab and explained — the same toast an edit on the canvas or transcript gets.
 * `display: contents`, so it adds no box to the layout it wraps.
 */
export const ClipVersionPreviewGuard = observer(
  ({
    adapter,
    children,
  }: {
    adapter?: ClipVideoCreatorAdapter | null;
    children: ReactNode;
  }) => {
    const block = (event: SyntheticEvent) => {
      if (!adapter?.versionPreview.isPreviewing) return;
      event.preventDefault();
      event.stopPropagation();
      adapter.versionPreview.explainPausedEdit();
    };
    return (
      <Guard
        onClickCapture={block}
        onPointerDownCapture={block}
        onBeforeInputCapture={block}
        data-testid="clip-version-preview-guard"
      >
        {children}
      </Guard>
    );
  },
);

// Tag: same shape as the clip modal's Published Version tag.
const Tag = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  width: max-content;
  padding: 2px 2px 2px 8px;
  border-radius: var(--radius);
`;

const Label = styled.span`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  white-space: nowrap;
  margin-right: 4px;
`;

const Detail = styled.span`
  font-weight: var(--font-weight-normal);
  color: var(--text-muted);
`;

const RestoreLink = styled.button`
  padding: 2px var(--spacing-xs);
  border: none;
  border-radius: 6px;
  background: transparent;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--primary);
  cursor: pointer;

  &:hover {
    background-color: var(--surface-elevated);
  }
`;

const ExitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  background: transparent;
  border: none;
  padding: var(--spacing-xs);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: var(--foreground);
    background-color: var(--surface-elevated);
  }
`;

const Guard = styled.div`
  display: contents;
`;
